import { MixpanelEvent, useDisclosure, useMixpanel } from '@talos/kyoko';
import { useCallback, useState } from 'react';
import { useAppStateDispatch, useAppStateSelector } from '../../../providers/AppStateProvider';
import {
  addNewLayoutToSection,
  redoChange,
  resetToInitialLayout,
  setCurrentLayout,
  undoChange,
} from '../AppLayoutSlice';
import { DEFAULT_TRADING_LAYOUT_SPEC } from '../Layouts/Trading/DefaultTradingLayoutSpec';

export const useEditLayoutControls = () => {
  const [newLayoutName, setNewLayoutName] = useState('');
  const undoStack = useAppStateSelector(state => state.appLayout.undoStack);
  const redoStack = useAppStateSelector(state => state.appLayout.redoStack);
  const layoutHasChanged = useAppStateSelector(state => state.appLayout.layoutHasChanged);
  const selectedLayout = useAppStateSelector(state => state.appLayout.currentLayout);
  const layoutSection = useAppStateSelector(state => state.appLayout.layoutSection);
  const layoutOptions = useAppStateSelector(state => state.appLayout.layoutOptions);
  const [layoutNameError, setLayoutNameError] = useState(false);
  const dispatch = useAppStateDispatch();
  const mixpanel = useMixpanel();

  const handleChangeLayout = useCallback(
    (layoutId: string) => {
      if (layoutOptions) {
        dispatch(setCurrentLayout({ layoutConfig: layoutOptions[layoutId] }));
        mixpanel.track(MixpanelEvent.ChangeLayout, { layout: layoutOptions[layoutId].label });
      }
    },
    [dispatch, layoutOptions, mixpanel]
  );

  const handleUndo = useCallback(() => {
    dispatch(undoChange());
    mixpanel.track(MixpanelEvent.UndoLayoutChange);
  }, [dispatch, mixpanel]);

  const handleRedo = useCallback(() => {
    dispatch(redoChange());
    mixpanel.track(MixpanelEvent.RedoLayoutChange);
  }, [dispatch, mixpanel]);

  const handleResetToInitial = useCallback(() => {
    dispatch(resetToInitialLayout());
    mixpanel.track(MixpanelEvent.ReloadLayout);
  }, [mixpanel, dispatch]);

  const saveNewDialog = useDisclosure();

  /** Legacy code, that should be removed after spaces are enabled for everyone */
  const handleSaveNewLayout = useCallback(() => {
    if (newLayoutName === '') {
      return false;
    }
    dispatch(addNewLayoutToSection({ label: newLayoutName, template: DEFAULT_TRADING_LAYOUT_SPEC, deviceType: 'all' }));
    saveNewDialog.close();
    setNewLayoutName('');
    mixpanel.track(MixpanelEvent.SaveNewLayout);
  }, [dispatch, saveNewDialog, newLayoutName, mixpanel]);

  return {
    undoStack,
    redoStack,
    layoutNameError,
    newLayoutName,
    saveNewDialog,
    layoutHasChanged,
    setLayoutNameError,
    handleUndo,
    handleRedo,
    handleResetToInitial,
    handleChangeLayout,
    setNewLayoutName,
    handleSaveNewLayout,
    selectedLayout,
    layoutSection,
    layoutOptions,
  };
};
